<script>
  import { onMount } from "svelte";
  import Slot from "./Slot.svelte";
  import { SPRITES } from "../../../constants/sprites";
  import { getTextsPlayerboards } from "../../../constants/allTexts";
  import {
    DecideStore,
    GameStateStore,
    Hearts,
    IsTurn,
    ItemsStore,
    PlayerId,
    PlayersStore,
    PotionsDiscardPile,
    PotionsStore,
    SetupStore,
    SwitchMonster,
    AlchemistCounter,
    MerchantTokenPosition,
    UsablePotions,
    RemoveStore,
    ItemsToPlace,
    SelectedActions,
  } from "../../stores/gameStore";
  import ComponentTiled from "../ComponentTiled.svelte";
  import ItemCard from "../ItemCard.svelte";
  import { ItemMeta } from "../../../constants/items";
  import PotionCard from "../PotionCard.svelte";
  import AlchemistToken from "../AlchemistToken.svelte";
  import MerchantToken from "../MerchantToken.svelte";
  import { onDestroy } from 'svelte';

  export let width,
    boardId,
    playerId,
    ownBoard = false,
    craftingTableFlip = false;

  $: moveAllowed =
    ownBoard && $GameStateStore?.possibleactions?.includes("moveItem");

  $: thisPlayersBoardID = $SetupStore?.playerboards[$PlayerId]?.board_ID;

  const board = $SetupStore?.data?.playerBoards?.[boardId];
  $: color = "#" + $PlayersStore[playerId]?.color;
  $: scale = width / SPRITES["PLAYERBOARD"].width;
  $: hearts = $Hearts?.[playerId] || 0;
  $: playerCards = [
      ...Object.values($ItemsStore).filter((c) => c.card_location == playerId),
      ...Object.values($PotionsStore).filter((c) => c.card_location == playerId),
    ];
  $: alchemistSkills = $AlchemistCounter;
  $: merchantTokenPosition = $MerchantTokenPosition;
  $: gameStateStore = $GameStateStore;
  $: thisPlayer = $PlayerId;
  $: canUsePotion = $UsablePotions[thisPlayer];

  let selectedCard = null;  
  let highlightedPositions = [];

  let toolTipOpen = [];
  function openTooltip(card) {
    if (toolTipOpen.idSelected === true) {
      return;
    }
    let idStandard = ''.concat(card.id, '_player',);
    let idEnhanced = ''.concat(card.id, '_player', '_enhanced');
    let idSelected = idStandard;
    if (card.enhanced != 1) {
      idSelected = idStandard;
    } else {
      idSelected = idEnhanced;
    }
    window.GAME.tooltips[idSelected].open(idSelected);
    toolTipOpen.idSelected = true;
  }

  function closeTooltip(card) {
    let idStandard = ''.concat(card.id, '_player',);
    let idEnhanced = ''.concat(card.id, '_player', '_enhanced');
    let idSelected = idStandard;
    if (card.enhanced != 1) {
      idSelected = idStandard;
    } else {
      idSelected = idEnhanced;
    }
    setTimeout(() => {
      window.GAME.tooltips[idSelected].close(idSelected);
      toolTipOpen.idSelected = false;
      }, 300);
  }

  // Function to handle card click
  function handleCardClick(card) {
    if (thisPlayersBoardID != boardId) {
      clearSelections();
      return;
    }
    if (selectedCard === card.id) {
      // Deselect if card is already selected
      clearSelections();
    } else {
      if ($GameStateStore.name === "decideCraftMaterial" || $GameStateStore.name === "makeSpace") {
        return;
      }
      setTimeout(() => {
        // Select card for movement and highlight positions
        selectedCard = card.id;
        if (moveAllowed) {
          highlightedPositions = getPossiblePositions(card);
        }
        addOutsideClickListener();
      }, 100); // Delay buttons becoming clickable
    }
  }

  // Function to handle card click
  function clearAfterButtonclick() {
    setTimeout(() => {
      clearSelections();
    }, 150); // Delay buttons becoming clickable
  }

  function getPossiblePositions(card) {
    const possiblePositions = [];
    const cardId = card.id;
    const cardSlot = card.slot;

    // Loop through each slot on the board to check droppable status
    board.slots.forEach((_, currSlot) => {
      const currCard = playerCards.find(c => c.slot === currSlot)?.id || null;

      // Check if this slot is a valid drop location
      if (checkDroppable(cardId, cardSlot, currCard, currSlot)) {
        possiblePositions.push(currSlot);
      }
    });

    return possiblePositions;
  } 
    
  // Clear selections if clicking outside a card or position
  function clearSelections() {
    selectedCard = null;
    highlightedPositions = [];
    removeOutsideClickListener();
  }

  // Add click listener to the entire document for clicks outside selected elements
  function addOutsideClickListener() {
    document.addEventListener("click", handleOutsideClick);
  }

  // Remove the document click listener
  function removeOutsideClickListener() {
    document.removeEventListener("click", handleOutsideClick);
  }

  // Handle clicks outside the selected elements
  function handleOutsideClick(event) {
    const clickedElement = event.target;

    // Check if the click was on a card, button, or highlighted position
    if (!clickedElement.closest(".card") &&
        !clickedElement.closest(".button") &&
        !clickedElement.closest(".highlighted")) {
      clearSelections();
    }
  }

  // Cleanup on component destroy
  onDestroy(() => {
    removeOutsideClickListener();
  });

  // Object to store hover states by card ID or slot index
  let hoverStates = {};

  // Function to enable button interaction for a specific card or slot
  function enableButtonInteraction(id) {
    hoverStates[id] = true;
  }

  // Function to disable button interaction for a specific card or slot
  function disableButtonInteraction(id) {
    hoverStates[id] = false;
  }

  const checkDroppable = (cardId, prevSlot, currCard, currSlot) => {
    // console.log(prevSlot);
    if (prevSlot == currSlot) return false;
    const newCard = $SetupStore.data.cards[cardId];
    const newSlot = board.slots[currSlot];
    const oldSlot = board.slots[prevSlot];

    const isDroppable =
      newSlot.type === "BAG" ||
      newSlot.type === newCard.position ||
      !newCard.position;
    if (!isDroppable) return false;
    if (!currCard) return true;
    const oldCard = $SetupStore.data.cards[currCard];
    if (oldSlot.type !== "BAG" && oldSlot.type !== oldCard.position && oldCard.position)
      return false;
    return true;
  };

  let id = boardId;
  onMount(() => {
    window.GAME.addTooltipHtml(
      id.toString(),
      `<div style="display:flex; flex-direction:column; max-width: 450px; word-wrap: break-word;">
        <div style="width:100%;display:flex;justify-content:space-between;align-items:center; gap:2rem;">
          <div class="headline">
            ${textsPlayerboards[boardId].name}
          </div>
        </div>
        <hr style="width:100%;"/>
        ${textsPlayerboards[boardId].description}
      </div>`
    );
  });

  let tradeItem = false;
  function closeTradeWindow() {
    tradeItem = false;
  }
  function tradePotion() {
    window.GAME.bgaPerformAction("craftPotionTrade", {
      cardId: tradeItem,
      playerId: $PlayerId,
    });
    tradeItem = false;
  }

  const textEnhance = _('ENHANCE');
  const textTrade = _('TRADE POTION');
  const textPotions = _('POTIONS');
  const textSwitch = _('Switch Creature');
  const textUse = _('USE');
  const textsPlayerboards = getTextsPlayerboards();
  const textSure = _('Are you sure?');
  const textExplanationPotiontrade = _('You will lose this item and get potions instead.');
  const textConfirm =_('Confirm');
  const textCancle = _('Cancel');
</script>

{#if !!board}
  <div style:border-color={color} class="wrapper shadow-md">
    <ComponentTiled
      {width}
      sprite={SPRITES["PLAYERBOARD"]}
      offset={board.offset}
      className="playerboard"
      id= {id}
    />
    <div class="textbox">
      <div class="charactername">{textsPlayerboards[boardId].name}</div>
    </div>
    {#each board.hearts as heart, i}
      <div
        style:position="absolute"
        style:left="{heart.offset[0] * scale}px"
        style:top="{heart.offset[1] * scale}px"
      >
        <ComponentTiled
          width={50 * scale}
          sprite={SPRITES["TOKENS"]}
          borderRadius={1}
          offset={i < hearts
            ? $SetupStore.data.gameTokens.HEART.offset
            : $SetupStore.data.gameTokens.HEART.offset_back}
          className="shadow-sm"
        />
      </div>
    {/each}

    {#if tradeItem}
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div class="dijitTooltipContainer tooltip" 
      class:visible={true}
      style:left="{50}px"
      style:top="{100}px"
      >
        <div style="display:flex; flex-direction:column; width: 350px; word-wrap: break-word;">
            <div class="headline">
              {textSure}
            </div>
          <hr style="width:100%;"/>
            {textExplanationPotiontrade}
          <hr style="width:100%;"/>
          <div style="display:flex; flex-direction:row; flex-wrap:wrap; gap:10px;">
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                class="btn check"
                style="
                  padding: 0.25rem 0.5rem;
                  background: white;
                  text-align: center;
                  font-size: 2.2rem;
                "
                on:click={tradePotion}
              >
                {textConfirm}
              </div>
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                class="btn check"
                style="
                  padding: 0.25rem 0.5rem;
                  background: white;
                  text-align: center;
                  font-size: 2.2rem;
                "
                on:click={closeTradeWindow}
              >
                {textCancle}
              </div>
          </div>
        </div>
      </div>
    {/if}

    <!-- Alchemist tokens -->
    {#if boardId === "ALCHEMIST_SPEC"}
      {#each board.potionSkills as skill, i}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
        <AlchemistToken
        tokenID={`ALCHEMIST_TOKEN_0${i+1}`}
        skill= {skill}
        scale= {scale}
        craftingTableFlip= {craftingTableFlip}
        />
      {/each}
    {/if}

    <!-- Merchant token -->
    {#if boardId === "MERCHANT_SPEC" && merchantTokenPosition === "playerboard"}
      <MerchantToken
      scale= {scale}
      left= {board.merchant_Token.offset[0] * scale}
      top= {board.merchant_Token.offset[1] * scale}
      />
    {/if}

    {#each board.slots as _slot, i}
      {@const card = playerCards.find((c) => c.slot == i)}
      <Slot
        id={_slot.cardId}
        left={_slot.offset[0] * scale}
        top={_slot.offset[1] * scale}
        width={SPRITES["ITEMS_FRONT"].width * scale * 0.5}
        height={SPRITES["ITEMS_FRONT"].height * scale * 0.5}
        onDrop={moveAllowed
          ? (e) => {
              e.preventDefault();
              const [_cardId, prevSlot] = e.dataTransfer.types[0].split("/");
              const cardId = _cardId.toUpperCase();
              // console.log(e);
              if (!checkDroppable(cardId, prevSlot, card?.id, i)) return;
              window.GAME.bgaPerformAction("moveItem", {
                cardId: cardId,
                slot: i,
                playerId: $PlayerId,
              },
                { checkAction: false });
              }
          : undefined}
        isDroppable={(cardId, prevSlot) =>
          checkDroppable(cardId.toUpperCase(), prevSlot, card?.id, i)}
      >
        {#if card}
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div
            class="cardWrapper"
            style:outline-color={ItemMeta[
              $SetupStore.data.cards[card.id].position || "UNDEFINED"
            ].color}
            on:mouseup|stopPropagation={() => handleCardClick(card)}
            on:mouseenter={() => enableButtonInteraction(card.id)}
            on:mouseleave={() => disableButtonInteraction(card.id)}
            class:decide={selectedCard === card.id}
          >
            {#if ($IsTurn) && (card.active === undefined) && (thisPlayer === playerId)}

              <!-- COMBINE -->
              {#if $GameStateStore.name === "decideCraftMaterial" && $DecideStore.craftingMaterial?.includes(card.id)}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <!-- svelte-ignore a11y-mouse-events-have-key-events -->
                <div
                  class="decide"
                  style:outline-color={$DecideStore.selected?.includes(card.id)
                    ? "red"
                    : "white"}
                  on:click={() => {
                    if ($DecideStore?.selected?.includes(card.id)) {
                      $DecideStore.selected = $DecideStore.selected.filter(
                        (s) => s !== card.id
                      );
                      $DecideStore = $DecideStore;
                    } else {
                      if ($DecideStore?.selected?.length === 2) return;
                      if (!$DecideStore.selected) $DecideStore.selected = [];
                      $DecideStore.selected.push(card.id);
                      $DecideStore = $DecideStore;
                    }
                  }}
                  on:mouseover={() => {
                    openTooltip(card);
                    }}
                  on:mouseleave={() => {
                    closeTooltip(card);
                    }}
                ></div>
              {/if}

              <!-- ENHANCE / TRADE -->
              {#if $GameStateStore.name === "actionCraft" ||
                  $GameStateStore.name === "craftOnce" ||
                  $GameStateStore.name === "potionEnhance2" ||
                  $GameStateStore.name === "potionEnhance1"
                  }
                <!-- svelte-ignore a11y-mouse-events-have-key-events -->
                <div
                class="buttons {hoverStates[card.id] || selectedCard === card.id ? 'visible' : ''}"
                style="z-index: 5"
                on:mouseover={() => {
                  openTooltip(card);
                  }}
                on:mouseleave={() => {
                  closeTooltip(card);
                  }}
                >
                  <button
                    class="enhance"
                    on:click={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.GAME.bgaPerformAction("craftEnhance", {
                        cardId: card.id,
                        playerId: $PlayerId,
                      });
                      clearAfterButtonclick();
                    }}
                    disabled={card.enhanced == 1 ||
                      !$SetupStore.data.cards[card.id].enhanced}
                  >
                    <div class="text">{textEnhance}</div></button
                  >
                  {#if
                    $GameStateStore.name === "actionCraft" ||
                    $GameStateStore.name === "craftOnce"
                    }
                    <button
                      class="trade"
                      on:click={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        tradeItem = card.id;
                        clearAfterButtonclick();
                      }}><div class="text">{textPotions}</div></button
                    >
                  {/if}
                </div>
              {/if}

              <!-- ACTIVATE ITEM IN INVENTORY -->
              {#if $GameStateStore.name === "potionActiveInInventory"}
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div
                class="decide"
                style:outline-color={"white"}
                on:click={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.GAME.bgaPerformAction("selectItemToActivate", {
                    cardId: card.id,
                  });
                }}
              ></div>
              {/if}

              <!-- SWITCH MONSTER -->
              {#if $GameStateStore.name === "startFight" && $SwitchMonster?.unusedItems?.[card.id] && $SelectedActions[0].action != ("ACTION_KING") && $SelectedActions[0].action != ("ACTION_ROGUE_04")}
                <div
                class="buttons_half {hoverStates[card.id] || selectedCard === card.id ? 'visible' : ''}"
                style="z-index: 5"
                >
                  <div style="pointer-events:none;flex:1;"></div>
                  <button
                    class="switch"
                    on:click={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.GAME.bgaPerformAction("switchMonster", {
                        cardId: card.id,
                        playerId: $PlayerId,
                      });
                      clearAfterButtonclick();
                    }}><div class="text">{textSwitch}</div></button
                  >
                </div>
              {/if}

            {/if}

            {#if ($IsTurn) && (thisPlayer === playerId)}

              <!-- REMOVE ITEMS -->
              {#if $GameStateStore.name === "makeSpace"}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <!-- svelte-ignore a11y-mouse-events-have-key-events -->
                <div
                  class="decide"
                  style:outline-color={$RemoveStore.selected?.includes(card.id)
                    ? "red"
                    : "white"}
                  on:click={() => {
                    if ($RemoveStore?.selected?.includes(card.id)) {
                      $RemoveStore.selected = $RemoveStore.selected.filter(
                        (s) => s !== card.id
                      );
                      $RemoveStore = $RemoveStore;
                    } else {
                      if ($RemoveStore?.selected?.length === $ItemsToPlace.length) return;
                      if (!$RemoveStore.selected) $RemoveStore.selected = [];
                      $RemoveStore.selected.push(card.id);
                      $RemoveStore = $RemoveStore;
                    }
                  }}
                  on:mouseover={() => {
                    openTooltip(card);
                    }}
                  on:mouseleave={() => {
                    closeTooltip(card);
                    }}
                ></div>
              {/if}
            {/if} 

            <!-- USE POTION -->
            {#if $GameStateStore?.possibleactions?.includes("usePotion") && (card.active !== undefined) && (thisPlayer === playerId) && (canUsePotion != 0)}
              <!-- {console.log('Card Active:', card.active)} -->
              {#if card.active != 1}
                <div 
                  class="buttons_half {hoverStates[card.id] || selectedCard === card.id ? 'visible' : ''}"
                  style="z-index: 5"
                >
                  <button
                    class="switch"
                    on:click={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.GAME.bgaPerformAction("usePotion", {
                        cardId: card.id,
                        playerId: $PlayerId,
                      },
                      { checkAction: false });
                      clearAfterButtonclick();
                      }}
                    ><div class="text">{textUse}</div>
                  </button>
                </div>
                {#if
                  ($IsTurn) &&
                  ($GameStateStore.name === "actionCraft" ||
                  $GameStateStore.name === "craftOnce")
                  }
                  <!-- svelte-ignore a11y-mouse-events-have-key-events -->
                  <div 
                    class="buttons_half_top {hoverStates[card.id] || selectedCard === card.id ? 'visible' : ''}"
                    style="z-index: 6"
                    on:mouseover={() => {
                      openTooltip(card);
                      }}
                    on:mouseleave={() => {
                      closeTooltip(card);
                      }}
                  >
                    <button
                      class="trade"
                      on:click={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        tradeItem = card.id;
                        clearAfterButtonclick();
                      }}><div class="text">{textTrade}</div>
                    </button>
                  </div>
                {/if}
              {/if}
            {/if}

            {#if ($IsTurn) && (card.active === undefined) && (thisPlayer != playerId)}

              <!-- UN-ENHANCE -->
              {#if ($GameStateStore.name === "potionUnenhance") && (card.enhanced == 1)}
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div
                class="decide"
                style:outline-color={"red"}
                on:click={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.GAME.bgaPerformAction("selectItemToUnenhance", {
                    cardId: card.id,
                  });
                }}
              ></div>
              {/if}

              <!-- BORROW ITEM -->
              {#if ($GameStateStore.name === "potionLendItem")}
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div
                class="decide"
                style:outline-color={"red"}
                on:click={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.GAME.bgaPerformAction("selectItemToLend", {
                    cardId: card.id,
                  });
                }}
              ></div>
              {/if}

            {/if}

            {#if card.active === undefined}
              <ItemCard
                width={SPRITES["ITEMS_FRONT"].width * scale * 0.5}
                card={$SetupStore.data.cards[card.id]}
                enhanced={card.enhanced}
                location={'player'}
                id={card.id}
                draggable={moveAllowed}
                ondragstart={(e) => {
                  // Hack to read data fron ondragover events
                  const enhancedValue = card.enhanced || "0";
                  e.dataTransfer.setData(card.id + "/" + i + "/" + enhancedValue, "");
                  e.dataTransfer.dropEffect = "move";
                }}
              />
            {:else}
              <PotionCard
                width={SPRITES["ITEMS_FRONT"].width * scale * 0.5}
                card={$SetupStore.data.cards[card.id]}
                active={card.active}
                location={'player'}
                id={card.id}
                draggable={moveAllowed}
                ondragstart={(e) => {
                  // Hack to read data fron ondragover events
                  e.dataTransfer.setData(card.id + "/" + i, "");
                  e.dataTransfer.dropEffect = "move";
                }}
              />
            {/if}
          </div>
        {/if}

        <!-- Hightlight Moveoptions on Click -->
        {#if (selectedCard) && (highlightedPositions.includes(i))}
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div
            class="decide"
            style:outline-color={"white"}
            on:click={(e) => {
              e.preventDefault();
              window.GAME.bgaPerformAction("moveItem", {
                cardId: selectedCard,
                slot: i,
                playerId: $PlayerId,
              },
                { checkAction: false });
            }}
          >
          </div>
        {/if}
        {#if card}
          {#if (selectedCard == card.id)}
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div
              class="decide"
              style="outline-color: red; pointer-events: none"
            >
            </div>
          {/if}
        {/if}

      </Slot>
    {/each}
  </div>
{/if}

<style>
  .wrapper {
    border: 4px solid white;
    border-radius: 10px;
    position: relative;
  }
  :global(.playerboard) {
    border-radius: 5px !important;
  }

  .cardWrapper {
    position: relative;
    border-radius: 10px;
  }

  .textbox {
    position: absolute;
    width: 120px;
    height: 40px;
    top: 1%;
    left: 75.7%;
  }
  .charactername {
    display: flex;
    justify-content: center;
    font-size: 41px;
    font-family: "Bigelow Rules", serif;
    font-weight: bold;
    font-variant: small-caps;
    margin: -0.5rem 0;
    transform: scale(1, 0.6);
    color: rgb(0, 0, 0);
  }

  .buttons {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    backdrop-filter: blur(2px);
    pointer-events: none; /* Disable clicks by default */
    opacity: 0;
  }

  .buttons_half {
    position: absolute;
    top: 60%;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    backdrop-filter: blur(2px);
    pointer-events: none; /* Disable clicks by default */
    opacity: 0;
  }

  .buttons_half_top {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 40%;
    display: flex;
    flex-direction: column;
    backdrop-filter: blur(2px);
    pointer-events: none; /* Disable clicks by default */
    opacity: 0;
  }
  

  .buttons.visible {
    opacity: 1;
    pointer-events: auto;
  }

  .buttons_half.visible {
    opacity: 1;
    pointer-events: auto;
  }

  .buttons_half_top.visible {
    opacity: 1;
    pointer-events: auto;
  }

  button {
    flex: 1;
    opacity: 0.6;
    cursor: pointer;
  }

  button:not(:disabled):hover {
    opacity: 1;
  }

  button:disabled {
    cursor: not-allowed;
  }

  button .text {
    font-family: "Bigelow Rules", serif;
    font-weight: bold;
    font-size: 1.8rem;
    margin: -0.5rem 0;
    transform: scale(1, 0.6);
  }

  .enhance {
    background: rgba(255, 255, 0, 0.8);
  }

  .trade {
    background: rgba(100, 150, 255, 0.8);
  }

  .switch {
    background: white;
  }

  .decide {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 5px;
    outline: 2px solid white;
    outline-offset: 2px;
    cursor: pointer;

    &:hover {
      backdrop-filter: brightness(1.2);
    }
  }

  .tooltip {
    position: absolute;
    padding: 10px;
    border-radius: 5px;
    z-index: 999;
    display: none;
    pointer-events: none;
  }

  .tooltip.visible {
    display: block;
    pointer-events: auto; /* Allow clicks on visible cards */
    gap: 10px; /* Optional: add some space between the cards */
  }
</style>
